import { RefObject, useEffect, useState } from "react";
import throttle from "lodash.throttle";

import { getContentPosition, PopupPosition } from "@multiweb/reactUtils";
import { IComponentSize } from "@multiweb/types/react";
import { IMaxSizes } from "./types";

const MAX_SIZES_THROTTLE_MS = 1000;

/**
 * Hook vracející maximální prostor dle získané pozice dropdownu.
 * @param triggerElement reference na spouštěcí element.
 * @param size Rozměry dropdown contentu.
 * @param isInitialized Přiznak, zda bylo dokončeno měření rozměrů dropdown contentu.
 * @returns Maximální výšku a šířku.
 */
export const useMaxSizes = (triggerElement: RefObject<HTMLDivElement>, isInitialized: boolean, size?: IComponentSize) => {
    const [maxSizes, setMaxSizes] = useState<IMaxSizes>();

    useEffect(() => {
        const handleResize = throttle(() => {
            if (!(triggerElement.current && size && isInitialized)) {
                return;
            }
            const contextElementRec = new DOMRect(0, 0, size.width, size.height);
            const triggerElementRec = triggerElement.current.getBoundingClientRect();
            const position = getContentPosition(triggerElementRec, contextElementRec);
            setMaxSizes(calcSizesForPosition(position, triggerElementRec));
        }, MAX_SIZES_THROTTLE_MS);

        const observer = new ResizeObserver(handleResize);
        observer.observe(document.body);
        return () => observer.disconnect();
    }, [triggerElement, size, isInitialized]);

    return maxSizes;
};

/**
 * Pomocná funkce pro výpočet maximálního prostoru dle zadané pozice.
 * @param popupPosition pozice dropdownu vůči spouštěcímu elementu.
 * @param triggerElementRec informace o velikosti a umístění spouštěcího elementu.
 * @returns Maximální výšku a šířku.
 */
const calcSizesForPosition = (popupPosition: PopupPosition, triggerElementRec: DOMRect): IMaxSizes => {
    switch (popupPosition) {
        case PopupPosition.rightBottom:
            return {
                height: window.innerHeight - triggerElementRec.top - triggerElementRec.height,
                width: window.innerWidth - triggerElementRec.left,
            };
        case PopupPosition.leftBottom:
            return {
                height: window.innerHeight - triggerElementRec.top - triggerElementRec.height,
                width: triggerElementRec.right,
            };
        case PopupPosition.leftTop:
            return {
                height: triggerElementRec.top,
                width: triggerElementRec.right,
            };
        case PopupPosition.rightTop:
            return {
                height: triggerElementRec.top,
                width: window.innerWidth - triggerElementRec.left,
            };
    }
};
